import React, { useState, useRef, ComponentType } from 'react'
import { EditorState } from 'draft-js'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ResizeableConfig {
  horizontal?: 'relative' | 'absolute' | 'auto'
  vertical?: 'relative' | 'absolute' | 'auto' | false
  resizeSteps?: number
  initialWidth?: string
  initialHeight?: string
  isResizable?: boolean
}

interface Store {
  getEditorRef?: () => any
  getReadOnly?: () => boolean
  getEditorState?: () => EditorState
  setEditorState?: (s: EditorState) => void
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const round = (x: number, steps: number) => Math.ceil(x / steps) * steps

const getDisplayName = (C: any) => {
  const c = C.WrappedComponent || C
  return c.displayName || c.name || 'Component'
}

/* ------------------------------------------------------------------ */
/*  Decorator (wraps the inner component with resize behaviour)        */
/* ------------------------------------------------------------------ */

function createDecorator({ config, store }: { config: ResizeableConfig; store: Store }) {
  const defaults: Required<ResizeableConfig> = {
    horizontal: 'relative',
    vertical: false,
    resizeSteps: 1,
    isResizable: true,
    initialWidth: '',
    initialHeight: '',
    ...config
  }

  return function decorator(WrappedComponent: ComponentType<any>) {
    const BlockResizeableDecorator = React.forwardRef<HTMLElement, any>(
      function BlockResizeableDecorator(props, _fwdRef) {
        const {
          blockProps,
          vertical = defaults.vertical,
          horizontal = defaults.horizontal,
          resizeSteps = defaults.resizeSteps,
          initialWidth = defaults.initialWidth,
          initialHeight = defaults.initialHeight,
          isResizable = defaults.isResizable,
          style,
          ...elementProps
        } = props

        const nodeRef = useRef<HTMLElement | null>(null)
        const storeRef = useRef(store)
        const [hoverPosition, setHoverPosition] = useState<Record<string, boolean>>({})
        const [clicked, setClicked] = useState(false)
        const [width, setWidth] = useState<number | undefined>(undefined)
        const [height, setHeight] = useState<number | undefined>(undefined)

        /* ---------- entity data persistence ---------- */

        const setEntityData = (data: { width?: number; height?: number }) => {
          blockProps.setResizeData(data)
        }

        /* ---------- mouse handlers ---------- */

        const isReadOnly = () => storeRef.current.getReadOnly?.() ?? true

        const mouseLeave = () => {
          if (!clicked) {
            setHoverPosition({})
          }
        }

        const mouseMove = (evt: React.MouseEvent) => {
          if (isReadOnly()) return
          const pane = nodeRef.current
          if (!pane) return

          const tolerance = 6
          const b = pane.getBoundingClientRect()
          const x = evt.clientX - b.left
          const y = evt.clientY - b.top

          const isTop = vertical && vertical !== 'auto' ? y < tolerance : false
          const isLeft = horizontal ? x < tolerance : false
          const isRight = horizontal ? x >= b.width - tolerance : false
          const isBottom = vertical && vertical !== 'auto' ? y >= b.height - tolerance && y < b.height : false
          const canResize = (isTop || isLeft || isRight || isBottom) && isResizable

          setHoverPosition((prev) => {
            if (
              prev.isTop === isTop &&
              prev.isLeft === isLeft &&
              prev.isRight === isRight &&
              prev.isBottom === isBottom &&
              prev.canResize === canResize
            ) {
              return prev
            }
            return { isTop, isLeft, isRight, isBottom, canResize }
          })
        }

        const mouseDown = (event: React.MouseEvent) => {
          if (isReadOnly()) return
          if (!hoverPosition.canResize) return
          event.preventDefault()

          const pane = nodeRef.current
          if (!pane) return

          const { isTop, isLeft, isRight, isBottom } = hoverPosition
          const startX = event.clientX
          const startY = event.clientY
          const startWidth = parseInt(document.defaultView!.getComputedStyle(pane).width, 10)
          const startHeight = parseInt(document.defaultView!.getComputedStyle(pane).height, 10)

          let latestWidth: number | undefined
          let latestHeight: number | undefined

          const doDrag = (dragEvent: MouseEvent) => {
            let w = startWidth + (isLeft ? startX - dragEvent.clientX : dragEvent.clientX - startX)
            let h = startHeight + dragEvent.clientY - startY

            const editorComp = storeRef.current.getEditorRef?.()
            const editorNode = editorComp?.refs?.editor ?? editorComp?.editor ?? editorComp
            if (editorNode) {
              w = Math.min(editorNode.clientWidth, w)
              h = Math.min(editorNode.clientHeight, h)
            }

            const widthPerc = (100 / (editorNode?.clientWidth ?? 1)) * w
            const heightPerc = (100 / (editorNode?.clientHeight ?? 1)) * h

            if ((isLeft || isRight) && horizontal === 'relative') {
              latestWidth = resizeSteps ? round(widthPerc, resizeSteps) : widthPerc
            } else if ((isLeft || isRight) && horizontal === 'absolute') {
              latestWidth = resizeSteps ? round(w, resizeSteps) : w
            }

            if ((isTop || isBottom) && vertical === 'relative') {
              latestHeight = resizeSteps ? round(heightPerc, resizeSteps) : heightPerc
            } else if ((isTop || isBottom) && vertical === 'absolute') {
              latestHeight = resizeSteps ? round(h, resizeSteps) : h
            }

            dragEvent.preventDefault()
            if (latestWidth !== undefined) setWidth(latestWidth)
            if (latestHeight !== undefined) setHeight(latestHeight)
          }

          const stopDrag = () => {
            document.removeEventListener('mousemove', doDrag, false)
            document.removeEventListener('mouseup', stopDrag, false)
            setClicked(false)
            setEntityData({ width: latestWidth, height: latestHeight })
          }

          document.addEventListener('mousemove', doDrag, false)
          document.addEventListener('mouseup', stopDrag, false)
          setClicked(true)
        }

        /* ---------- compute inline styles ---------- */

        const { isTop, isLeft, isRight, isBottom } = hoverPosition

        const styles: Record<string, any> = { position: 'relative', ...style }

        if (horizontal === 'auto') {
          styles.width = 'auto'
        } else if (horizontal === 'relative') {
          const v = width ?? blockProps.resizeData?.width
          styles.width = !v && initialWidth ? initialWidth : `${v || 40}%`
        } else if (horizontal === 'absolute') {
          const v = width ?? blockProps.resizeData?.width
          styles.width = !v && initialWidth ? initialWidth : `${v || 40}px`
        }

        if (vertical === 'auto') {
          styles.height = 'auto'
        } else if (vertical === 'relative') {
          const v = height ?? blockProps.resizeData?.height
          styles.height = !v && initialHeight ? initialHeight : `${v || 40}%`
        } else if (vertical === 'absolute') {
          const v = height ?? blockProps.resizeData?.height
          styles.height = !v && initialHeight ? initialHeight : `${v || 40}%`
        }

        // cursor
        if (!isResizable) {
          styles.cursor = 'default'
        } else if ((isRight && isBottom) || (isLeft && isTop)) {
          styles.cursor = 'nwse-resize'
        } else if ((isRight && isTop) || (isBottom && isLeft)) {
          styles.cursor = 'nesw-resize'
        } else if (isRight || isLeft) {
          styles.cursor = 'ew-resize'
        } else if (isBottom || isTop) {
          styles.cursor = 'ns-resize'
        } else {
          styles.cursor = 'default'
        }

        const interactionProps = {
          onMouseDown: mouseDown,
          onMouseMove: mouseMove,
          onMouseLeave: mouseLeave
        }

        const setRef = (el: HTMLElement | null) => {
          nodeRef.current = el
          // forward ref if provided
          if (typeof _fwdRef === 'function') _fwdRef(el)
          else if (_fwdRef) (_fwdRef as React.MutableRefObject<HTMLElement | null>).current = el
        }

        return (
          <WrappedComponent
            {...elementProps}
            {...interactionProps}
            blockProps={blockProps}
            ref={setRef}
            style={styles}
          />
        )
      }
    )

    BlockResizeableDecorator.displayName = `Resizable(${getDisplayName(WrappedComponent)})`
    ;(BlockResizeableDecorator as any).WrappedComponent = (WrappedComponent as any).WrappedComponent || WrappedComponent

    return BlockResizeableDecorator
  }
}

/* ------------------------------------------------------------------ */
/*  setResizeData helper (persists width/height to Draft entity)       */
/* ------------------------------------------------------------------ */

function createSetResizeData(
  contentBlock: any,
  { getEditorState, setEditorState }: { getEditorState: () => EditorState; setEditorState: (s: EditorState) => void }
) {
  return (data: Record<string, any>) => {
    const entityKey = contentBlock.getEntityAt(0)
    if (entityKey) {
      const editorState = getEditorState()
      const contentState = editorState.getCurrentContent()
      contentState.mergeEntityData(entityKey, { ...data })
      setEditorState(EditorState.forceSelection(editorState, editorState.getSelection()))
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Public factory (drop-in for createResizeablePlugin)                */
/* ------------------------------------------------------------------ */

export default function createResizeablePlugin(config: ResizeableConfig = {}) {
  const store: Store = {
    getEditorRef: undefined,
    getReadOnly: undefined,
    getEditorState: undefined,
    setEditorState: undefined
  }

  return {
    initialize({
      getEditorRef,
      getReadOnly,
      getEditorState,
      setEditorState
    }: {
      getEditorRef: () => any
      getReadOnly: () => boolean
      getEditorState: () => EditorState
      setEditorState: (s: EditorState) => void
    }) {
      store.getReadOnly = getReadOnly
      store.getEditorRef = getEditorRef
      store.getEditorState = getEditorState
      store.setEditorState = setEditorState
    },

    decorator: createDecorator({ config, store }),

    blockRendererFn(
      contentBlock: any,
      {
        getEditorState,
        setEditorState
      }: { getEditorState: () => EditorState; setEditorState: (s: EditorState) => void }
    ) {
      const entityKey = contentBlock.getEntityAt(0)
      const contentState = getEditorState().getCurrentContent()
      const resizeData = entityKey ? contentState.getEntity(entityKey).getData() : {}
      return {
        props: {
          resizeData,
          setResizeData: createSetResizeData(contentBlock, { getEditorState, setEditorState })
        }
      }
    }
  }
}
